import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Button, Typography, Paper, Table, TableHead,
  TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent,
  IconButton, TablePagination, TextField, MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AddEditOtherMiscFee from './AddEditOtherMiscFee';
import { API_GET_MISC_FEES } from '../Config/ApiUrlConstant';
import { useAuth } from '../AuthContext';

const OtherFeeManagement = () => {
  const [fees, setFees] = useState([]);
  const [filteredFees, setFilteredFees] = useState([]);
  const [open, setOpen] = useState(false);
  const [editFee, setEditFee] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { logout } = useAuth();

  // filters
  const [filterClass, setFilterClass] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterFather, setFilterFather] = useState('');

  const fetchOtherFees = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(API_GET_MISC_FEES, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }

      const data = await res.json();
      setFees(data.data || []);
      setFilteredFees(data.data || []);
    } catch (error) {
      console.error('Error fetching other fees:', error);
    }
  };

  useEffect(() => {
    fetchOtherFees();
  }, []);

  // Apply filters whenever search values change
  useEffect(() => {
    let temp = fees;
    if (filterClass) {
      temp = temp.filter(f => String(f.class).toLowerCase().includes(filterClass.toLowerCase()));
    }
    if (filterName) {
      temp = temp.filter(f => f.student_name?.toLowerCase().includes(filterName.toLowerCase()));
    }
    if (filterFather) {
      temp = temp.filter(f => f.student_father_name?.toLowerCase().includes(filterFather.toLowerCase()));
    }
    setFilteredFees(temp);
    setPage(0);
  }, [filterClass, filterName, filterFather, fees]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = e => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleEdit = (fee) => {
    setEditFee(fee);
    setOpen(true);
  };

  return (
    <Box>
      <Grid container justifyContent="space-between" alignItems="center" mb={2}>
        <Grid item>
          <Typography variant="h5">Miscellaneous Fee List</Typography>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={() => { setEditFee(null); setOpen(true); }}>
            Add Misc Fee
          </Button>
        </Grid>
      </Grid>

      {/* 🔍 Filters Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} style={{width:'150px'}}>
            <TextField
              label="Filter by Class"
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              fullWidth
              select
            >
              <MenuItem value="">All</MenuItem>
              {[...new Set(fees.map(f => f.class))].map((cls, idx) => (
                <MenuItem key={idx} value={cls}>{cls}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Filter by Student Name"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Filter by Father Name"
              value={filterFather}
              onChange={(e) => setFilterFather(e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Student ID</TableCell>
              <TableCell>Student Name</TableCell>
              <TableCell>Father Name</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Book Amt</TableCell>
              <TableCell>Lower T-shirt Amt</TableCell>
              <TableCell>Admission</TableCell>
              <TableCell>Annual Promotion</TableCell>
              <TableCell>Diary/ID/Tie/Belt</TableCell>
              <TableCell>Misc Paid</TableCell>
              <TableCell>Misc Pending</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Session</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((fee, index) => (
              <TableRow key={index}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{fee.student_no}</TableCell>
                <TableCell>{fee.student_name}</TableCell>
                <TableCell>{fee.student_father_name}</TableCell>
                <TableCell>{fee.class}</TableCell>
                <TableCell>{fee.book_paid_amt}</TableCell>
                <TableCell>{fee.lower_tshirt_paid_amt}</TableCell>
                <TableCell>{fee.admission_amt}</TableCell>
                <TableCell>{fee.annual_promotion_amt}</TableCell>
                <TableCell>{fee.diary_id_tie_belt_amt}</TableCell>
                <TableCell>
                  {parseFloat(fee.book_paid_amt) +
                   parseFloat(fee.lower_tshirt_paid_amt) +
                   parseFloat(fee.admission_amt) +
                   parseFloat(fee.annual_promotion_amt) +
                   parseFloat(fee.diary_id_tie_belt_amt) -
                   parseFloat(fee.discount) -
                   parseFloat(fee.misc_pending)}
                </TableCell>
                <TableCell>{fee.misc_pending}</TableCell>
                <TableCell>{fee.description}</TableCell>
                <TableCell>{fee.session}</TableCell>
                <TableCell>{fee.created_at?.split('T')[0]}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleEdit(fee)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredFees.length === 0 && (
              <TableRow>
                <TableCell colSpan={16} align="center">No records found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredFees.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {editFee ? "Edit Misc Fee" : "Add Misc Fee"}
          <IconButton onClick={() => setOpen(false)} style={{ width: '100px' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <AddEditOtherMiscFee
            initialData={editFee}
            onSuccess={() => { fetchOtherFees(); setOpen(false); }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default OtherFeeManagement;
